import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { 
  selectedCountryChangingAction, 
  fetchCountryDetails 
} from "../../store/slice/SelectedCountrySlice";
import axiosInstance from "../../external-library/axios-interceptor/Interceptor";
import { homeServerUrl } from "../../environment/Environment";
import VerticalNavigation from "../../components/verticalNavigation/verticalNavigation.tsx";
import Dropdown from "../../components/dropdown/dropdown.tsx";
import StyleButton from "../../components/styleButton/styleButton.tsx";
import Tooltip from "../../components/tooltip/tooltip.tsx";
import Slider from "../../components/slider/slider.tsx";
import { useAppContext } from "../../data/context.tsx";
import { useWizardTags } from "../../data/useWizardTags.ts";
import { getRegionsOfCountry, REGION_PROPERTY_NAME } from "../../services/FeatureInfoService.js";
import type { TagState, CountryDetails } from "../../data/types.ts";
import "./goalSetting.css";

const GoalSetting: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [countries, setCountries] = useState<string[]>([]);
  const [regionOptions, setRegionOptions] = useState<string[]>([]);
  const [isRegionLoading, setIsRegionLoading] = useState(false);
  const {
    state,
    setCountry,
    setRegion,
    setObjective,
    updateMultipleFocusAreas,
  } = useAppContext();

  const countryDetails = useSelector((state: any) => state.SelectedCountrySlice.countryDetails as CountryDetails);

  useEffect(() => {
    const fetchCountriesList = async () => {
      try {
        const response = await axiosInstance.get(`${homeServerUrl}/countries`);
        const countryNames = response.data.map((c: any) => c.name || c.Name);
        setCountries(countryNames);
      } catch (error) {
        console.error("Failed to fetch countries", error);
      }
    };
    fetchCountriesList();
  }, []);

  const lastFetchedCountryRef = useRef<string | null>(null);
  const selectedRegionRef = useRef<string | null>(state.selectedRegion || null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    selectedRegionRef.current = state.selectedRegion || null;
  }, [state.selectedRegion]);

  const tags = useWizardTags();

  const objectives = [
    {
      name: "Profit Maximization",
      tooltipText:
        "Transform waste into a source of high financial return. Prioritize business models that deliver strong profitability by minimizing costs. The goal is to generate maximum income from available waste streams while ensuring economic sustainability.",
    },
    {
      name: "Yield Maximization",
      tooltipText:
        "Extract the highest possible value from waste resources. Focus on efficiency and productivity by choosing processes that maximize outputs, such as energy, materials, or nutrients, with minimal losses. The objective is to make full use of every component in the waste stream.",
    },
    {
      name: "Welfare Optimization",
      tooltipText:
        "Create positive social impact through waste transformation. Evaluate business models considering their ability to generate decent jobs, support livelihoods, and contribute to equitable development. The goal is to ensure that circular bioeconomy solutions improve community well-being and promote inclusive growth.",
    },
    {
      name: "Environmental Maximization",
      tooltipText:
        "Minimize environmental harm while enhancing ecosystem health. Prioritize models that reduce emissions, conserve natural resources, and regenerate biodiversity. The objective is to turn waste into a driver of environmental restoration and long-term planetary sustainability.",
    },
    {
      name: "Enabling Environment",
      tooltipText:
        "Operate within a context that supports success. Identify and align with favorable policies, infrastructure, institutions, and partnerships. The goal is to ensure the chosen business model thrives within a strong, supportive ecosystem for circular and sustainable innovation.",
    },
  ];

  const objectiveSliderPresets: {
    [key: string]: {
      technology: number;
      environment: number;
      people: number;
      finance: number;
      institutionsAndMarket: number;
    };
  } = {
    "Profit Maximization": {
      technology: 20,
      environment: 10,
      people: 10,
      finance: 40,
      institutionsAndMarket: 20,
    },
    "Yield Maximization": {
      technology: 40,
      environment: 10,
      people: 10,
      finance: 20,
      institutionsAndMarket: 20,
    },
    "Welfare Optimization": {
      technology: 10,
      environment: 30,
      people: 40,
      finance: 10,
      institutionsAndMarket: 10,
    },
    "Environmental Maximization": {
      technology: 10,
      environment: 40,
      people: 30,
      finance: 10,
      institutionsAndMarket: 10,
    },
    "Enabling Environment": {
      technology: 10,
      environment: 10,
      people: 10,
      finance: 30,
      institutionsAndMarket: 40,
    },
  };

  const fetchRegionsForCountry = useCallback(
    async (details: CountryDetails, selectedRegion?: string | null) => {
      setRegionOptions([]);
      setIsRegionLoading(true);

      const layerName = details.regionLayerName;

      const requestId = ++requestIdRef.current;

      const res = await getRegionsOfCountry(layerName);

      if (requestId !== requestIdRef.current) {
        return;
      }

      const uniqueRegions = Array.from(
        new Set(
          res.data.features
            .map((f) => f.properties[REGION_PROPERTY_NAME])
            .map((name) => name.split(",")[0].trim())
        )
      ).sort();

      setRegionOptions(uniqueRegions);

      const regionToValidate =
        selectedRegion ?? selectedRegionRef.current ?? "";

      if (regionToValidate && !uniqueRegions.includes(regionToValidate)) {
        setRegion("");
      }

      setIsRegionLoading(false);
    },
    [setRegion]
  );

  const handleCountryChange = async (country: string) => {
    if (country === state.selectedCountry) {
      if (regionOptions.length === 0) {
        lastFetchedCountryRef.current = country;
        if (countryDetails) {
          await fetchRegionsForCountry(countryDetails, selectedRegionRef.current);
        }
      }
      return;
    }

    setCountry(country);
    setRegion("");
    dispatch(selectedCountryChangingAction(country));
    dispatch(fetchCountryDetails(country));

    // Let the useEffect pick up the new countryDetails and fetch regions
  };

  useEffect(() => {
    if (!countryDetails) return;
    const currentName = countryDetails.name;
    if (lastFetchedCountryRef.current === currentName) return;
    lastFetchedCountryRef.current = currentName;
    fetchRegionsForCountry(countryDetails, selectedRegionRef.current);
  }, [countryDetails, fetchRegionsForCountry]);

  const handleRegionChange = (region: string) => {
    setRegion(region);
  };

  const handleObjectiveSelect = (objective: string) => {
    if (!state.selectedRegion) return;

    if (state.selectedObjective === objective) {
      setObjective("");
      return;
    }

    setObjective(objective);

    const preset = objectiveSliderPresets[objective];
    if (preset) {
      updateMultipleFocusAreas(preset);
    }
  };

  const handleContinue = () => {
    navigate("/business-model");
  };

  const handleBack = () => {
    navigate("/");
  };

  const handleSliderChange = (changedKey: string, newValue: number) => {
    const currentValues = { ...state.focusAreas };
    const oldValue = currentValues[changedKey as keyof typeof currentValues];
    const difference = newValue - oldValue;

    // If no change, return early
    if (difference === 0) return;

    // Get all other keys
    const otherKeys = Object.keys(currentValues).filter(
      (key) => key !== changedKey
    );

    // Calculate how much to adjust each other slider
    const adjustmentPerSlider = -difference / otherKeys.length;

    // Create new values object
    const newValues = { ...currentValues };
    newValues[changedKey as keyof typeof newValues] = newValue;

    // Adjust other sliders
    otherKeys.forEach((key) => {
      const currentVal = newValues[key as keyof typeof newValues];
      const adjustedValue = Math.max(
        0,
        Math.min(100, currentVal + adjustmentPerSlider)
      );
      newValues[key as keyof typeof newValues] = adjustedValue;
    });

    // Due to rounding and clamping, the sum might not be exactly 100, so let's normalize
    normalizeValues(newValues, changedKey, newValue);
  };

  // Function to normalize values to ensure sum is exactly 100
  const normalizeValues = (
    values: any,
    changedKey: string,
    targetValue: number
  ) => {
    const currentSum = Object.values(values).reduce(
      (sum: number, val: any) => sum + val,
      0
    );
    const difference = 100 - currentSum;

    if (Math.abs(difference) > 0.01) {
      // Only normalize if there's a significant difference
      const otherKeys = Object.keys(values).filter((key) => key !== changedKey);
      const adjustmentPerSlider = difference / otherKeys.length;

      otherKeys.forEach((key) => {
        const currentVal = values[key as keyof typeof values];
        const adjustedValue = Math.max(
          0,
          Math.min(100, currentVal + adjustmentPerSlider)
        );
        values[key as keyof typeof values] = adjustedValue;
      });

      // Final check and update
      const finalSum = Object.values(values).reduce(
        (sum: number, val: any) => sum + val,
        0
      );
      if (Math.abs(100 - finalSum) > 0.01) {
        // If still not perfect, adjust the largest value to make it exact
        const largestKey = Object.keys(values).reduce((a, b) =>
          values[a as keyof typeof values] > values[b as keyof typeof values]
            ? a
            : b
        );
        values[largestKey as keyof typeof values] += 100 - finalSum;
      }
    }

    // Update all values using the batch update function
    updateMultipleFocusAreas(values);
  };

  return (
    <div className="goalSetting-container">
      <VerticalNavigation
        title="Goal Setting"
        description="The final results of the analysis will depend on your country, goal and preferences."
        tags={tags}
        currentStep={1}
        totalSteps={4}
        onMenuToggle={setIsMenuOpen}
        onBack={handleBack}
      />
      {!isMenuOpen && (
        <div className="goalSetting-content">
          <div className="goalSetting-inner">
            <div className="goalSetting-columns">
              <div className="goalSetting-column-left">
                <div className="goalSetting-fields-row">
                  <div className="goalSetting-field">
                    <h3 className="goalSetting-field-title">Country</h3>
                    <Dropdown
                      placeholder="Select a country"
                      options={countries}
                      value={state.selectedCountry}
                      onChange={handleCountryChange}
                    />
                  </div>

                  <div className="goalSetting-field">
                    <h3 className="goalSetting-field-title">Region</h3>
                    <Dropdown
                      placeholder="Select a region"
                      isLoading={isRegionLoading}
                      options={regionOptions}
                      value={state.selectedRegion}
                      onChange={handleRegionChange}
                      disabled={!state.selectedCountry || isRegionLoading}
                      disabledText={
                        !state.selectedCountry
                          ? "Select country first"
                          : isRegionLoading
                          ? "Loading regions..."
                          : undefined
                      }
                    />
                  </div>
                </div>

                <div className="goalSetting-objective-section">
                  <div className="goalSetting-field-title-with-tooltip">
                    <h3 className="goalSetting-field-title">
                      Analysis Objective
                    </h3>
                    <Tooltip
                      title="Analysis Objective"
                      content="Choose the main goal of your analysis."
                    />
                  </div>
                  <div className="goalSetting-objective-buttons">
                    {objectives.map((objective) => (
                      <StyleButton
                        key={objective.name}
                        content={objective.name}
                        isSelected={state.selectedObjective === objective.name}
                        onClick={() => handleObjectiveSelect(objective.name)}
                        backgroundColorSelected="var(--color-green)"
                        tooltipText={objective.tooltipText}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div className="goalSetting-column-right">
                <div className="goalSetting-focus-areas-box">
                  <div className="goalSetting-focus-areas-header">
                    <h3 className="goalSetting-field-title">
                      Edit your focus areas
                    </h3>
                    <Tooltip
                      title="Edit Focus Areas"
                      content="Customize your priorities by adjusting the weight of five key areas: Technology, People, Environment, Finance, and Institutions & Market. Tailor the decision to match what matters most to you. REMEMBER: the total should be 100%."
                    />
                  </div>
                  <div className="goalSetting-sliders">
                    <Slider
                      title="Technology"
                      value={state.focusAreas.technology}
                      onChange={(value) =>
                        handleSliderChange("technology", value)
                      }
                      disabled={!state.selectedObjective}
                      tooltip="Focus on the technical feasibility and maturity of the solution. Consider how innovative, scalable, and efficient the technology is for converting waste into value. Prioritize this area if you're interested in models that rely on cutting-edge processes, automation, or proven systems ready for implementation."
                    />

                    <Slider
                      title="People"
                      value={state.focusAreas.people}
                      onChange={(value) => handleSliderChange("people", value)}
                      disabled={!state.selectedObjective}
                      tooltip="Emphasize the social impact of your circular business model. Consider job creation, gender inclusion, community engagement, and improved livelihoods. Choose this focus if you value how the solution supports local people, strengthens social equity, and contributes to long-term well-being."
                    />

                    <Slider
                      title="Environment"
                      value={state.focusAreas.environment}
                      onChange={(value) =>
                        handleSliderChange("environment", value)
                      }
                      disabled={!state.selectedObjective}
                      tooltip="Prioritize environmental sustainability by assessing how the solution affects natural resources, emissions, biodiversity, and waste reduction. Focus here if your main goal is to restore ecosystems, reduce your environmental footprint, or contribute to climate and conservation goals through circular practices."
                    />
                    <Slider
                      title="Finance"
                      value={state.focusAreas.finance}
                      onChange={(value) => handleSliderChange("finance", value)}
                      disabled={!state.selectedObjective}
                      tooltip="Concentrate on the financial performance of the business model. This includes cost-effectiveness, profitability and return on investment. Increase the weight of this area if you want to ensure strong economic viability and attractive returns from your circular bioeconomy venture."
                    />
                    <Slider
                      title="Institutions & Market"
                      value={state.focusAreas.institutionsAndMarket}
                      onChange={(value) =>
                        handleSliderChange("institutionsAndMarket", value)
                      }
                      disabled={!state.selectedObjective}
                      tooltip="Focus on how supportive the external environment is for your business model. Consider policies, regulations, infrastructure, partnerships, and market demand. Prioritize this if you're looking for solutions that align with existing systems and have strong potential for adoption and scaling in your context."
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="goalSetting-button-container">
              <button
                className={`goalSetting-continue-button ${
                  state.selectedObjective
                    ? "goalSetting-continue-button--active"
                    : "goalSetting-continue-button--disabled"
                }`}
                onClick={handleContinue}
                disabled={!state.selectedObjective}
              >
                Business Model →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoalSetting;
